/**
 * OccupancyChart.tsx
 * ------------------
 * Forecast accuracy chart for a journey.
 * Data source: vu_message_log / vu_message_uic / vu_message_carriage
 *
 * Place at: src/analysis/OccupancyChart.tsx
 *
 * Chart behaviour:
 *   X-axis     : stop sequence order, stop_name labels
 *   IST line   : solid amber — actual occupancy at each completed stop
 *   PROGNOSE   : one dashed coloured line per selected origin stop,
 *                showing its forecast to N+5
 *   Interaction: click any completed stop marker to toggle its PROGNOSE series
 *   Uncompleted stops: dimmed on x-axis, no data points
 *   Tooltip    : stop name, time, IST value, all active PROGNOSE values,
 *                sensor source badges
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import {
  Box, Chip, CircularProgress,
  IconButton, Typography,
} from '@mui/material';
import RefreshIcon    from '@mui/icons-material/Refresh';

import {
  fetchOccupancy,
  fmtDepTime, stopShortName, prognoseColour, sourceColour, istTotal,
  OccupancyResponse, OccupancyStop,
} from './occupancyApi';

// ---------------------------------------------------------------------------
// Palette — matches dashboard dark theme
// ---------------------------------------------------------------------------
const C = {
  bg:        '#0d1b2a',
  bgInner:   '#0a1520',
  border:    'rgba(240,165,0,0.15)',
  amber:     '#f0a500',
  dimText:   'rgba(255,255,255,0.22)',
  mutedText: 'rgba(255,255,255,0.45)',
  bodyText:  'rgba(255,255,255,0.82)',
  gridLine:  'rgba(255,255,255,0.05)',
  tooltipBg: 'rgba(8,18,30,0.98)',
  cancelled: '#f87171',
  ist:       '#f0a500',   // amber — IST line
};

function stufeColour(stufe: string | null | undefined): string {
  if (stufe === 'NIEDRIG') return '#4ade80';   // green
  if (stufe === 'MITTEL')  return '#f0a500';   // amber
  if (stufe === 'HOCH')    return '#f87171';   // red
  return C.ist;                                 // default amber
}

// ---------------------------------------------------------------------------
// Chart data shape
// ---------------------------------------------------------------------------
interface ChartPoint {
  label: string;            // x-axis key
  seq: number;
  stop_name: string | null;
  stop_place_eva: string;
  scheduled_time: string | null;
  completed: boolean;
  cancelled: boolean;
  has_vu: boolean;          // vu_message exists for this stop
  type_message_output: string | null;
  ist: number | undefined;  // undefined = no data → gap in line
  ist_auslastungsstufe: string | null;  // ← add
  // prognose series keyed by origin stop_sequence e.g. "p5", "p6"
  [key: string]: any;
}



// ---------------------------------------------------------------------------
// Build chart data from API response
// ---------------------------------------------------------------------------
function buildChartData(
  stops: OccupancyStop[],
  activeOrigins: Set<number>,
): ChartPoint[] {
  const seqToStop = new Map(stops.map(s => [s.stop_sequence, s]));

  return stops.map(stop => {
    const pt: ChartPoint = {
      label:               stopShortName(stop) + (stop.stop_cancelled ? ' ✕' : ''),
      seq:                 stop.stop_sequence,
      stop_name:           stop.stop_name,
      stop_place_eva:      stop.stop_place_eva,
      scheduled_time:      stop.scheduled_departure_time,
      completed:           stop.completed,
      cancelled:           stop.stop_cancelled,
      has_vu:              stop.type_message_output != null,
      type_message_output: stop.type_message_output,
      ist:                 stop.completed && stop.ist_belegung != null
                             ? stop.ist_belegung
                             : undefined,
      ist_auslastungsstufe: stop.ist_auslastungsstufe,
    };

    // For each active origin stop, check if this stop is in its prognose series
    activeOrigins.forEach(originSeq => {
      const origin = seqToStop.get(originSeq);
      if (!origin) return;
      if (stop.stop_sequence === originSeq) {
        // Anchor the forecast line at the origin's IST (actual) value so the
        // dashed line visibly starts from the current stop, not the first forecast.
        pt[`p${originSeq}`] = pt.ist;
      } else {
        const pp = origin.prognose.find(p => p.target_seq === stop.stop_sequence);
        pt[`p${originSeq}`] = pp?.total_belegung ?? undefined;
      }
    });

    return pt;
  });
}

// ---------------------------------------------------------------------------
// Custom tooltip
// ---------------------------------------------------------------------------
interface TooltipProps {
  active?: boolean;
  payload?: any[];
  label?: string;
  chartData: ChartPoint[];
  activeOrigins: Set<number>;
  stops: OccupancyStop[];
}

const CustomTooltip: React.FC<TooltipProps> = ({
  active, payload, label, chartData, activeOrigins, stops,
}) => {
  if (!active || !payload?.length) return null;
  const pt = chartData.find(d => d.label === label);
  if (!pt) return null;

  const seqToStop = new Map(stops.map(s => [s.stop_sequence, s]));

  return (
    <Box sx={{
      backgroundColor: C.tooltipBg,
      border: `1px solid ${C.border}`,
      borderRadius: '6px',
      px: 1.5, py: 1,
      minWidth: '200px',
      maxWidth: '260px',
    }}>
      <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '11px', color: C.bodyText, fontWeight: 600, mb: 0.5 }}>
        {pt.stop_name ?? pt.stop_place_eva}
      </Typography>
      {pt.scheduled_time && (
        <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', color: C.mutedText, mb: 0.75 }}>
          {fmtDepTime(pt.scheduled_time)}
        </Typography>
      )}

      {!pt.completed && (
        <Typography sx={{ fontFamily: '"IBM Plex Sans"', fontSize: '11px', color: C.dimText, fontStyle: 'italic' }}>
          Noch nicht abgefahren
        </Typography>
      )}

      {pt.ist != null && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '11px', color: stufeColour(pt.ist_auslastungsstufe) }}>
            IST {pt.type_message_output ? `(${pt.type_message_output})` : ''}
          </Typography>
          <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '11px', color: stufeColour(pt.ist_auslastungsstufe) }}>
            {pt.ist}
          </Typography>
        </Box>
      )}

      {/* Forecast quality by horizon, only where the actual is known.
          A forecast emitted at stop s targeting this stop t is at horizon
          k = t - s — the same definition the accuracy-service evaluates on, so
          these read the same way as the N+1..N+5 figures on the sidebar.

          Independent of activeOrigins: those are the series the user clicked,
          this is every horizon that produced a forecast for this stop. The
          percentage is signed against the actual, so + means over-forecast.

          Colour follows the same convention as the per-series diff below —
          direction, not magnitude. No accuracy threshold is applied here
          because none has been measured for belegung. */}
      {pt.ist != null && pt.ist > 0 && (() => {
        const rows = [1, 2, 3, 4, 5].map(k => {
          const origin = seqToStop.get(pt.seq - k);
          const pp = origin?.prognose.find(x => x.target_seq === pt.seq);
          const val = pp?.total_belegung ?? null;
          if (val == null) return { k, val, accuracy: null, diff: null };
          const diff = val - pt.ist!;
          // Accuracy, not error: 1 - |predicted - actual| / actual. 55 actual
          // against 64 predicted reads 83.6%, which is the question being asked
          // ("how good was that forecast"), where -16.4% answers a different
          // one. Floored at 0 so a wild over-forecast cannot read as a large
          // negative, which would sort and colour as though it were a value.
          const accuracy = Math.max(0, 1 - Math.abs(diff) / pt.ist!) * 100;
          return { k, val, accuracy, diff };
        }).filter(r => r.val != null);

        if (!rows.length) return null;

        return (
          <Box sx={{ mt: 0.75, pt: 0.75, borderTop: `1px solid ${C.border}` }}>
            <Typography sx={{
              fontFamily: '"IBM Plex Mono"', fontSize: '9px', color: C.mutedText,
              letterSpacing: '0.08em', textTransform: 'uppercase', mb: 0.25,
            }}>
              Prognosegüte je Horizont
            </Typography>
            {rows.map(r => (
              <Box key={r.k} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', color: C.mutedText }}>
                  N+{r.k}
                </Typography>
                <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', color: C.bodyText }}>
                  {r.val}
                  {/* The rating carries no colour band. The app's ≥90 / ≥70
                      thresholds belong to CLASS hit rate, a different quantity;
                      no accuracy band has been measured for belegung, so one is
                      not invented here. The signed delta keeps its direction
                      colour, which is the existing convention below. */}
                  <span style={{ marginLeft: 6, fontWeight: 600 }}>
                    {r.accuracy!.toFixed(1)}%
                  </span>
                  <span style={{
                    color: r.diff! > 0 ? '#f87171' : r.diff! < 0 ? '#4ade80' : C.mutedText,
                    marginLeft: 6,
                  }}>
                    ({r.diff! > 0 ? '+' : ''}{r.diff})
                  </span>
                </Typography>
              </Box>
            ))}
          </Box>
        );
      })()}

      {Array.from(activeOrigins).map((originSeq, i) => {
        const val = pt[`p${originSeq}`];
        // Skip the origin's own anchor point (equals its IST) — not a forecast
        if (val == null || originSeq === pt.seq) return null;
        const origin = seqToStop.get(originSeq);
        const colour = prognoseColour(i);
        const diff = pt.ist != null ? val - pt.ist : null;
        return (
          <Box key={originSeq} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
            <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', color: colour }}>
              ↦ von Halt {originSeq}
            </Typography>
            <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', color: C.bodyText }}>
              {val}
              {diff != null && (
                <span style={{ color: diff > 0 ? '#f87171' : '#4ade80', marginLeft: 4 }}>
                  ({diff > 0 ? '+' : ''}{diff})
                </span>
              )}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};


// ---------------------------------------------------------------------------
// Prop types for custom Recharts components
// ---------------------------------------------------------------------------
interface XTickProps {
  x: number;
  y: number;
  payload: { value: string };
  chartData: ChartPoint[];
  activeOrigins: Set<number>;
}

interface DotProps {
  cx: number;
  cy: number;
  payload: ChartPoint;
  onToggle: (seq: number) => void;
  activeOrigins: Set<number>;
}

// ---------------------------------------------------------------------------
// Custom x-axis tick
// ---------------------------------------------------------------------------
const CustomXTick: React.FC<XTickProps> = ({ x, y, payload, chartData, activeOrigins }) => {
  const pt = chartData?.find((d: ChartPoint) => d.label === payload?.value);
  const incomplete = pt && !pt.completed;
  const cancelled  = pt?.cancelled;
  const isOrigin   = pt && activeOrigins?.has(pt.seq);

  return (
    <g transform={`translate(${x},${y})`}>
      {isOrigin && (
        <circle cx={0} cy={-8} r={4} fill={prognoseColour(
          Array.from(activeOrigins as Set<number>).indexOf(pt.seq)
        )} />
      )}
      <text
        transform="rotate(-45)"
        textAnchor="end"
        dominantBaseline="middle"
        style={{
          fontSize: '10px',
          fontFamily: '"IBM Plex Mono", monospace',
          fill: cancelled  ? C.cancelled
              : incomplete ? C.dimText
              : isOrigin   ? C.bodyText
              : C.mutedText,
          fontWeight: isOrigin ? 600 : 400,
        }}
      >
        {payload?.value}
      </text>
    </g>
  );
};

// ---------------------------------------------------------------------------
// Clickable dot on the IST line — toggles PROGNOSE series
// ---------------------------------------------------------------------------
const ClickableDot: React.FC<DotProps> = ({ cx, cy, payload, onToggle, activeOrigins }) => {
  if (!payload?.completed || payload?.ist == null) return null;
  const isActive = activeOrigins?.has(payload.seq);
  const idx = Array.from(activeOrigins as Set<number>).indexOf(payload.seq);

  return (
    <circle
      cx={cx} cy={cy} r={isActive ? 6 : 4}
      fill={isActive ? prognoseColour(idx) : stufeColour(payload.ist_auslastungsstufe)}
      stroke={isActive ? 'white' : 'transparent'}
      strokeWidth={1.5}
      style={{ cursor: 'pointer' }}
      onClick={() => onToggle?.(payload.seq)}
    />
  );
};

// ---------------------------------------------------------------------------
// UIC breakdown panel
// ---------------------------------------------------------------------------
const UicBreakdown: React.FC<{
  stop: OccupancyStop;
  onClose: () => void;
}> = ({ stop, onClose }) => (
  <Box sx={{
    mt: 1, p: 1.5,
    backgroundColor: 'rgba(255,255,255,0.03)',
    borderRadius: '6px',
    border: `1px solid ${C.border}`,
  }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
      <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', color: C.amber }}>
        {stop.stop_name} — IST UICs
      </Typography>
      <Typography
        sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', color: C.mutedText, cursor: 'pointer' }}
        onClick={onClose}
      >✕</Typography>
    </Box>
    {stop.ist_uics.map((uic, i) => (
      <Box key={uic.uic} sx={{ mb: 0.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
          <Box sx={{
            width: 6, height: 6, borderRadius: '50%',
            backgroundColor: stufeColour(uic.auslastungsstufe) ?? sourceColour(uic.sensor_source),
          }} />
          <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', color: C.bodyText }}>
            {uic.uic}
          </Typography>
          <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', color: C.mutedText }}>
            {uic.sensor_source ?? '?'}
          </Typography>
          <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', color: stufeColour(uic.auslastungsstufe), ml: 'auto' }}>
            {uic.belegung ?? '—'}
          </Typography>
        </Box>
        {uic.carriages.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, ml: 2 }}>
            {uic.carriages.map(c => (
              <Box key={c.carriage_index} sx={{
                px: 0.75, py: 0.25,
                backgroundColor: 'rgba(255,255,255,0.04)',
                borderRadius: '3px',
                border: `1px solid ${stufeColour(c.auslastungsstufe)}40`,  // 25% opacity border
              }}>
                <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '9px', color: C.mutedText }}>
                  W{c.carriage_index}
                </Typography>
                <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', color: stufeColour(c.auslastungsstufe) }}>
                  {c.belegung ?? '—'}
                </Typography>                
              </Box>
            ))}
          </Box>
        )}
      </Box>
    ))}
  </Box>
);

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
interface OccupancyChartProps {
  journeyId: string;
}

const OccupancyChart: React.FC<OccupancyChartProps> = ({ journeyId }) => {
  const [data,          setData]          = useState<OccupancyResponse | null>(null);
  const [loading,       setLoading]       = useState(false);
  const [error,         setError]         = useState<string | null>(null);
  const [lastFetch,     setLastFetch]     = useState<Date | null>(null);
  const [activeOrigins, setActiveOrigins] = useState<Set<number>>(new Set());
  const [uicStop,       setUicStop]       = useState<OccupancyStop | null>(null);
  const [showUicPanel,  setShowUicPanel]  = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchOccupancy(journeyId);
      setData(res);
      setLastFetch(new Date());
      setActiveOrigins(new Set());  // reset selections on reload
    } catch (e: any) {
      setError(e?.message ?? 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [journeyId]);

  useEffect(() => {
    setData(null);
    setActiveOrigins(new Set());
    setUicStop(null);
    load();
  }, [journeyId, load]);

  const toggleOrigin = useCallback((seq: number) => {
    setActiveOrigins(prev => {
      const next = new Set(prev);
      if (next.has(seq)) {
        next.delete(seq);
      } else {
        next.add(seq);
      }
      return next;
    });
    // Show UIC breakdown for clicked stop
    if (data) {
      const stop = data.stops.find(s => s.stop_sequence === seq);
      if (stop) {
        setUicStop(stop);
        setShowUicPanel(true);
      }
    }
  }, [data]);

  const chartData = useMemo(
    () => data ? buildChartData(data.stops, activeOrigins) : [],
    [data, activeOrigins],
  );

  // Last completed stop index for reference line
  const lastCompletedLabel = useMemo(() => {
    const completed = chartData.filter(d => d.completed);
    return completed.length ? completed[completed.length - 1].label : null;
  }, [chartData]);

  // Active PROGNOSE series as ordered array for stable legend colours
  const activeOriginsArr = useMemo(
    () => Array.from(activeOrigins).sort((a, b) => a - b),
    [activeOrigins],
  );

  // ---------------------------------------------------------------------------
  // Header
  // ---------------------------------------------------------------------------
  const header = (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
      <Box>
        <Typography sx={{
          fontFamily: '"IBM Plex Mono"', fontSize: '11px',
          letterSpacing: '0.1em', textTransform: 'uppercase',
          color: C.amber, mb: 0.25,
        }}>
          Belegungsanalyse
        </Typography>
        {lastFetch && (
          <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', color: C.dimText }}>
            {loading ? 'Aktualisierung…' : `Stand: ${lastFetch.toLocaleTimeString('de-DE', { timeZone: 'Europe/Berlin' })}`}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {activeOrigins.size > 0 && (
          <Typography
            sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', color: C.mutedText, cursor: 'pointer' }}
            onClick={() => setActiveOrigins(new Set())}
          >
            Prognosen zurücksetzen
          </Typography>
        )}
        <IconButton onClick={load} disabled={loading} size="small" sx={{ color: C.mutedText, '&:hover': { color: C.amber } }}>
          <RefreshIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  );

  // ---------------------------------------------------------------------------
  // Stat chips
  // ---------------------------------------------------------------------------
  const statChips = data && (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
      <Chip
        size="small"
        label={`${data.stops.filter(s => s.completed).length} / ${data.stops.length} Halte`}
        sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', backgroundColor: 'rgba(255,255,255,0.05)', color: C.mutedText, borderRadius: '4px' }}
      />
      {data.stops.filter(s => s.completed && s.ist_belegung != null).length > 0 && (
        <Chip
          size="small"
          label={`IST max: ${Math.max(...data.stops.filter(s => s.ist_belegung != null).map(s => s.ist_belegung!))}`}
          sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', backgroundColor: 'rgba(240,165,0,0.1)', color: C.amber, borderRadius: '4px', border: `1px solid rgba(240,165,0,0.2)` }}
        />
      )}
      {activeOrigins.size > 0 && (
        <Chip
          size="small"
          label={`${activeOrigins.size} Prognose-Serie${activeOrigins.size > 1 ? 'n' : ''} aktiv`}
          sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', backgroundColor: 'rgba(56,189,248,0.1)', color: '#38bdf8', borderRadius: '4px', border: '1px solid rgba(56,189,248,0.2)' }}
        />
      )}
      <Chip
        size="small"
        label="Halt anklicken → Prognose"
        sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', backgroundColor: 'rgba(255,255,255,0.03)', color: C.dimText, borderRadius: '4px' }}
      />
    </Box>
  );

  // ---------------------------------------------------------------------------
  // States
  // ---------------------------------------------------------------------------
  if (loading && !data) return (
    <Box sx={{ backgroundColor: C.bg, borderRadius: '8px', p: 3 }}>
      {header}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 4 }}>
        <CircularProgress size={16} sx={{ color: C.amber }} />
        <Typography sx={{ fontFamily: '"IBM Plex Sans"', fontSize: '13px', color: C.mutedText }}>
          Daten werden geladen…
        </Typography>
      </Box>
    </Box>
  );

  if (error) return (
    <Box sx={{ backgroundColor: C.bg, borderRadius: '8px', p: 3 }}>
      {header}
      <Box sx={{ border: '1px solid rgba(248,113,113,0.25)', borderRadius: '6px', p: 2, backgroundColor: 'rgba(248,113,113,0.06)' }}>
        <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '11px', color: '#f87171', mb: 0.5 }}>Fehler beim Laden</Typography>
        <Typography sx={{ fontFamily: '"IBM Plex Sans"', fontSize: '12px', color: C.mutedText }}>{error}</Typography>
      </Box>
    </Box>
  );

  if (!data || chartData.length === 0) return (
    <Box sx={{ backgroundColor: C.bg, borderRadius: '8px', p: 3 }}>
      {header}
      <Typography sx={{ fontFamily: '"IBM Plex Sans"', fontSize: '13px', color: C.mutedText }}>
        Keine VU-Daten verfügbar.
      </Typography>
    </Box>
  );

  // ---------------------------------------------------------------------------
  // Chart
  // ---------------------------------------------------------------------------
  return (
    <Box sx={{ backgroundColor: C.bg, borderRadius: '8px', p: 3 }}>
      {header}
      {statChips}

      <ResponsiveContainer width="100%" height={360}>
        <LineChart
          data={chartData}
          margin={{ top: 8, right: 24, left: 0, bottom: 90 }}
        >
          <CartesianGrid stroke={C.gridLine} strokeDasharray="3 3" vertical={false} />

          <XAxis
            dataKey="label"
            tick={(props: any) => (
              <CustomXTick
                {...props}
                chartData={chartData}
                activeOrigins={activeOrigins}
              />
            )}
            interval={0}
            height={90}
            axisLine={{ stroke: 'rgba(255,255,255,0.08)' }}
            tickLine={{ stroke: 'rgba(255,255,255,0.06)' }}
          />

          <YAxis
            tick={{ fill: C.mutedText, fontFamily: '"IBM Plex Mono"', fontSize: 10 }}
            axisLine={false}
            tickLine={false}
            width={36}
            label={{
              value: 'Fahrgäste',
              angle: -90,
              position: 'insideLeft',
              offset: 10,
              style: { fill: C.dimText, fontFamily: '"IBM Plex Mono"', fontSize: 9 },
            }}
          />

          <Tooltip
            content={
              <CustomTooltip
                chartData={chartData}
                activeOrigins={activeOrigins}
                stops={data.stops}
              />
            }
            cursor={{ stroke: 'rgba(240,165,0,0.15)', strokeWidth: 1 }}
            /* The wrapper sits ABOVE the dots, so without this it swallows the
               click that toggles a forecast series on and off. It only became
               noticeable when the tooltip grew a horizon block and started
               covering the dots far more often — the hit area was always there,
               it was just small enough to miss. */
            wrapperStyle={{ pointerEvents: 'none' }}
          />

          <Legend
            wrapperStyle={{ fontFamily: '"IBM Plex Mono"', fontSize: '11px', color: C.mutedText, paddingTop: '8px' }}
          />

          {lastCompletedLabel && (
            <ReferenceLine
              x={lastCompletedLabel}
              stroke="rgba(240,165,0,0.25)"
              strokeDasharray="4 4"
              label={{
                value: 'jetzt',
                position: 'top',
                style: { fill: 'rgba(240,165,0,0.4)', fontFamily: '"IBM Plex Mono"', fontSize: 9 },
              }}
            />
          )}

          {/* IST line */}
          <Line
            type="monotone"
            dataKey="ist"
            name="IST"
            stroke={C.ist}
            strokeWidth={2.5}
            connectNulls={false}
            dot={(props: any) => (
              <ClickableDot
                {...props}
                onToggle={toggleOrigin}
                activeOrigins={activeOrigins}
              />
            )}
            activeDot={false}
          />

          {/* PROGNOSE series — one per active origin */}
          {activeOriginsArr.map((originSeq, i) => {
            const originStop = data.stops.find(s => s.stop_sequence === originSeq);
            const modelTag = originStop?.model_version ? ` (${originStop.model_version})` : '';
            return (
            <Line
              key={`p${originSeq}`}
              type="monotone"
              dataKey={`p${originSeq}`}
              name={`Prognose Halt ${originSeq}${modelTag}`}
              stroke={prognoseColour(i)}
              strokeWidth={1.5}
              strokeDasharray="5 3"
              connectNulls={false}
              dot={(props: any) => {
                const { cx, cy, payload, key } = props;
                // Anchor sits on the IST dot — don't draw a forecast dot there
                if (payload?.seq === originSeq) return <g key={key} />;
                return <circle key={key} cx={cx} cy={cy} r={3} fill={prognoseColour(i)} stroke="none" />;
              }}
              activeDot={{ r: 5 }}
            />
            );
          })}
        </LineChart>
      </ResponsiveContainer>

      {/* UIC breakdown panel */}
      {showUicPanel && uicStop && uicStop.ist_uics.length > 0 && (
        <UicBreakdown
          stop={uicStop}
          onClose={() => setShowUicPanel(false)}
        />
      )}

      {/* Legend note */}
      {/* Legend note */}
      <Box sx={{ mt: 1.5, display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', color: C.dimText }}>
          <span style={{ color: C.ist }}>━━</span>&nbsp; IST (tatsächliche Belegung)
        </Typography>
        <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', color: C.dimText }}>
          <span style={{ color: '#38bdf8' }}>╌╌</span>&nbsp; Prognose (Klick auf IST-Punkt zum Anzeigen)
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#38bdf8' }} />
          <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', color: C.dimText }}>AFZ</Typography>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#4ade80', ml: 1 }} />
          <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', color: C.dimText }}>WiFi</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#4ade80' }} />
          <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', color: C.dimText }}>NIEDRIG</Typography>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#f0a500', ml: 1 }} />
          <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', color: C.dimText }}>MITTEL</Typography>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#f87171', ml: 1 }} />
          <Typography sx={{ fontFamily: '"IBM Plex Mono"', fontSize: '10px', color: C.dimText }}>HOCH</Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default OccupancyChart;
